/**
 * The module's own learning outcomes.
 *
 * @module components/studio/panels/OutcomesPanel
 */

import React from "react";

import { MimloLint } from "@/components/studio/MimloLint";
import type { ModuleEditorState } from "@/components/studio/useModuleEditor";
import {
  Button,
  EmptyState,
  IconButton,
  Panel,
  Text,
  useEditLock,
  useFocusOnAdd,
} from "@/components/ui";
import { useConfirm } from "@/components/ui/ConfirmProvider";
import { TextAreaField } from "@/components/ui/Form";
import { DEFAULT_GUIDANCE } from "@/taxonomy/registry";
import type { MIMLO } from "@/types";

import { OutcomeProvenance, type OutcomeSources } from "./OutcomeProvenance";

const NO_SOURCES: OutcomeSources = new Map();

export function OutcomesPanel({
  mimlos,
  nfqLevel,
  editor,
  sources = NO_SOURCES,
}: {
  mimlos: readonly MIMLO[];
  /** Outcomes imported from a CAS component, by id. */
  sources?: OutcomeSources;
  nfqLevel: number | null;
  editor: ModuleEditorState;
}): React.ReactElement {
  const confirm = useConfirm();
  const focusOnAdd = useFocusOnAdd();
  // The lock leaves the Add button off, so the empty state gives its reason instead.
  const lock = useEditLock();

  const remove = async (mimlo: MIMLO, index: number): Promise<void> => {
    const proceed = await confirm({
      title: `Remove module learning outcome ${index + 1}?`,
      body: "This removes the outcome and its assessment mappings from the module.",
      confirmLabel: "Remove outcome",
      tone: "danger",
      "data-testid": "module-remove-mimlo-confirm",
    });
    if (proceed) {
      editor.removeMimlo(mimlo.id);
    }
  };

  return (
    <Panel
      aria-label="Module learning outcomes"
      description="Write the minimum intended module learning outcomes for this module."
      actions={
        <Button
          variant="outline"
          icon="plus"
          onClick={() => {
            // The guidance panel above the list is tall enough to put a new
            // row below the fold, so appending alone looks like nothing
            // happened. Focusing it says where it went and scrolls it in.
            focusOnAdd(`module-workspace-mimlo-${editor.addMimlo()}`);
          }}
          data-testid="module-workspace-add-mimlo"
        >
          Add outcome
        </Button>
      }
    >
      <DEFAULT_GUIDANCE.Panel level={nfqLevel} contextLabel="module outcomes" />
      <div className="mt-5 divide-y divide-base-300">
        {mimlos.length ? (
          mimlos.map((mimlo, index) => (
            <div
              key={mimlo.id}
              className="grid gap-3 py-4 md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-start"
            >
              <Text as="span" tone="muted" className="mt-3">
                {index + 1}
              </Text>
              <div>
                <label className="sr-only" htmlFor={`module-workspace-mimlo-${mimlo.id}`}>
                  Module learning outcome {index + 1}
                </label>
                <TextAreaField
                  id={`module-workspace-mimlo-${mimlo.id}`}
                  className="min-h-24"
                  value={mimlo.text}
                  onChange={(value) => editor.updateMimlo(mimlo.id, value)}
                  data-testid={`module-workspace-mimlo-${index + 1}`}
                />
                <MimloLint text={mimlo.text} nfqLevel={nfqLevel} />
                <OutcomeProvenance
                  index={index}
                  source={sources.get(mimlo.id)}
                  onRestore={(text) => editor.updateMimlo(mimlo.id, text)}
                  onNote={(note) => editor.setCasDeviationNote(mimlo.id, note)}
                />
              </div>
              <IconButton
                icon="trash"
                variant="ghost"
                className="mt-1"
                aria-label={`Remove outcome ${index + 1}`}
                onClick={() => remove(mimlo, index)}
              />
            </div>
          ))
        ) : (
          <EmptyState
            icon="graduation-cap"
            title="No module outcomes yet"
            description={
              lock.locked ? lock.reason : "Add the outcomes this module is assessed against."
            }
          />
        )}
      </div>
    </Panel>
  );
}

export default OutcomesPanel;
